import { EmbeddedRecord, SearchQuery, SearchResult } from '../types/index.js';

// Use the centralized transformers loader
import { loadTransformers } from './transformers-init.js';

export class EmbeddingService {
  private embeddingPipeline: any = null;
  private modelName = 'all-MiniLM-L6-v2';
  private embeddedRecords: EmbeddedRecord[] = [];
  private isInitialized = false;
  private initializationFailed = false;

  /**
   * Initialize the embedding pipeline
   */
  async init() {
    if (this.isInitialized || this.initializationFailed) return;

    console.log(`🤖 Loading local MiniLM-L6-v2 (384-dim, superior semantic): ${this.modelName}`);

    try {
      // Load transformers module dynamically
      const { pipeline } = await loadTransformers();

      console.log('📥 Loading embedding pipeline...');

      // Try with explicit device and execution provider settings
      this.embeddingPipeline = await pipeline('feature-extraction', this.modelName, {
        progress_callback: (progress: { status?: string; name?: string; progress?: number }) => {
          console.log('Pipeline progress:', progress);
          if (progress.status === 'downloading') {
            console.log(`📥 Downloading ${progress.name}: ${Math.round(progress.progress || 0)}%`);
          } else if (progress.status === 'loading') {
            console.log(`🔄 Loading ${progress.name}...`);
          }
        },
        cache_dir: './public/models',
        local_files_only: true,
        device: 'wasm',
        dtype: 'q8',
        quantized: true,
      });

      this.isInitialized = true;
      console.log(`✅ MiniLM-L6-v2 loaded successfully: ${this.modelName}`);
    } catch (error) {
      console.error('❌ Failed to load embedding model:', error);
      const err = error as unknown;
      if (err instanceof Error) {
        console.error('❌ Error details:', {
          message: err.message,
          name: err.name,
          stack: err.stack,
          cause: err.cause,
        });

        // Check for specific ONNX runtime errors
        if (err.message.includes("Can't create a session")) {
          console.error('🔧 ONNX Session Error - this could be due to:');
          console.error('   - Model file corruption');
          console.error('   - WASM runtime initialization failure');
          console.error('   - Memory constraints');
          console.error('   - Browser compatibility issues');
          console.error('   - Model format incompatibility');
        }
      } else {
        console.error('❌ Unknown error details:', err);
      }
      this.initializationFailed = true;

      // Set global flag for UI to know embeddings are unavailable
      if (typeof window !== 'undefined') {
        window.__EMBEDDINGS_AVAILABLE__ = false;
        window.__EMBEDDINGS_UNAVAILABLE__ = true;
      }

      // Don't throw - let the app continue with reduced functionality
      console.warn(
        '⚠️ Semantic search will be DISABLED. App will continue with limited functionality.'
      );
    }
  }

  /**
   * Generate embeddings for a batch of texts
   */
  async generateEmbeddings(texts: string[]): Promise<number[][]> {
    if (this.initializationFailed || !this.isInitialized || !this.embeddingPipeline) {
      console.warn('🔄 Embedding model not available, cannot generate embeddings');
      throw new Error('Embedding model not initialized');
    }

    console.log(`🔄 Generating embeddings for ${texts.length} texts...`);

    const embeddings: number[][] = [];

    // Process texts individually using the pipeline
    for (let i = 0; i < texts.length; i++) {
      const text = texts[i];

      try {
        // Generate embedding using the pipeline with mean pooling and normalization
        const output = await this.embeddingPipeline(text, {
          pooling: 'mean',
          normalize: true,
        });

        // Convert tensor to regular array
        const embedding = Array.from(output.data) as number[];
        embeddings.push(embedding);
      } catch (error) {
        console.warn(`⚠️ Failed to generate embedding for text ${i}:`, error);
        // Push zero vector as fallback
        embeddings.push(new Array(384).fill(0));
      }

      // Progress tracking
      if (i % 50 === 0 || i === texts.length - 1) {
        console.log(`  Processed ${i + 1}/${texts.length} texts`);
      }
    }

    console.log(`✅ Generated embeddings for ${embeddings.length} texts`);
    return embeddings;
  }

  /**
   * Generate embedding for a single text
   */
  async generateEmbedding(text: string): Promise<number[]> {
    const embeddings = await this.generateEmbeddings([text]);
    return embeddings[0];
  }

  /**
   * Load precomputed embeddings from public directory (no generation needed!)
   */
  async loadEmbeddedRecords(): Promise<void> {
    console.log('📚 Loading data with proper embedding matching...');

    try {
      // Load precomputed embeddings and their corresponding records (maintains correct order)
      const [embeddingsResponse, recordsResponse] = await Promise.all([
        fetch('/embeddings/embeddings.json'),
        fetch('/embeddings/records.json'),
      ]);

      if (!embeddingsResponse.ok || !recordsResponse.ok) {
        throw new Error('Failed to load precomputed embeddings');
      }

      const embeddings = await embeddingsResponse.json();
      const records = await recordsResponse.json();

      console.log(`📊 Loaded ${records.length} records and ${embeddings.length} embeddings`);

      // Create embedded records with precomputed embeddings and norms
      this.embeddedRecords = records.map((recordWrapper: any, index: number) => {
        const embedding = embeddings[index];
        const norm = this.computeNorm(embedding);

        // The JSON structure is: { "record": { actual_record }, ... }
        // Extract the actual record which now has up-to-date sentiment data
        const actualRecord = recordWrapper.record;

        return {
          record: actualRecord, // Contains updated sentiment data
          embedding,
          norm,
          embedded_text: recordWrapper.embedded_text || actualRecord.search_text,
        };
      });

      // Debug: Show type breakdown
      const typeBreakdown = this.embeddedRecords.reduce((acc: any, er) => {
        acc[er.record.type] = (acc[er.record.type] || 0) + 1;
        return acc;
      }, {});
      console.log(
        `✅ Loaded ${this.embeddedRecords.length} precomputed embeddings with correct order`
      );
      console.log(`📊 Debug: Type breakdown:`, typeBreakdown);
    } catch (error) {
      console.error('❌ Failed to load precomputed embeddings:', error);
      console.warn('⚠️ Falling back to empty results');
      this.embeddedRecords = [];
    }
  }

  /**
   * Compute L2 norm of a vector
   */
  private computeNorm(vector: number[]): number {
    return Math.sqrt(vector.reduce((sum, val) => sum + val * val, 0));
  }

  /**
   * Compute cosine similarity between two vectors
   */
  private computeCosineSimilarity(a: number[], b: number[], normA: number, normB: number): number {
    let dotProduct = 0;
    for (let i = 0; i < a.length; i++) {
      dotProduct += a[i] * b[i];
    }
    return dotProduct / (normA * normB);
  }

  /**
   * Compute token and n-gram contributions via mean-pooling decomposition.
   *
   * MiniLM uses mean pooling: emb = mean(h_1..h_N). This means each token's
   * contribution to dot(emb, target) is analytically decomposable as dot(h_i, target)/N.
   * This gives us the same attribution insight as leave-one-out ablation but with
   * 2 forward passes instead of ~21 per rule.
   */
  async getTokenContributions(queryText: string, targetText: string): Promise<Map<string, number>> {
    if (this.initializationFailed || !this.isInitialized || !this.embeddingPipeline) {
      return new Map();
    }

    // Single forward pass: get per-token hidden states (no pooling)
    const tokenOutput = await this.embeddingPipeline(queryText, {
      pooling: 'none',
      normalize: false,
    });

    // Single forward pass: get target embedding
    const targetEmbedding = await this.generateEmbedding(targetText);
    const targetNorm = this.computeNorm(targetEmbedding);

    // Extract tensor dimensions: [1, seq_len, hidden_dim]
    const seqLen = tokenOutput.dims[1];
    const hiddenDim = tokenOutput.dims[2];
    const data: Float32Array = tokenOutput.data;

    // Get tokenizer to map subword tokens back to words
    const tokenizer = this.embeddingPipeline.tokenizer;
    const encoded = tokenizer(queryText);
    const inputIds = Array.from(encoded.input_ids.data) as number[];

    // Decode each token (skip [CLS] at 0 and [SEP] at end)
    const numContentTokens = seqLen - 2;
    if (numContentTokens <= 0) return new Map();

    // Compute per-token dot products with target vector
    const tokenDots: number[] = [];
    for (let i = 1; i <= numContentTokens; i++) {
      let dot = 0;
      const offset = i * hiddenDim;
      for (let j = 0; j < hiddenDim; j++) {
        dot += data[offset + j] * targetEmbedding[j];
      }
      // Normalize: contribution to cosine similarity ≈ dot(h_i, target) / (N * |target|)
      tokenDots.push(dot / (numContentTokens * targetNorm));
    }

    // Decode individual subword tokens
    const tokenTexts: string[] = [];
    for (let i = 1; i <= numContentTokens; i++) {
      tokenTexts.push(
        tokenizer.decode([inputIds[i]], { skip_special_tokens: true }).trim()
      );
    }

    // Group subword tokens (WordPiece ##-prefixed) back to whole words
    const words: string[] = [];
    const wordScores: number[] = [];
    let currentWord = '';
    let currentScore = 0;

    for (let i = 0; i < tokenTexts.length; i++) {
      const token = tokenTexts[i];
      if (token.startsWith('##')) {
        // Subword continuation
        currentWord += token.slice(2);
        currentScore += tokenDots[i];
      } else {
        if (currentWord) {
          words.push(currentWord.toLowerCase());
          wordScores.push(currentScore);
        }
        currentWord = token;
        currentScore = tokenDots[i];
      }
    }
    if (currentWord) {
      words.push(currentWord.toLowerCase());
      wordScores.push(currentScore);
    }

    // Build contributions map: unigrams, bigrams, trigrams
    const contributions = new Map<string, number>();

    for (let i = 0; i < words.length; i++) {
      contributions.set(words[i], wordScores[i]);
    }

    for (let i = 0; i < words.length - 1; i++) {
      contributions.set(
        `${words[i]} ${words[i + 1]}`,
        wordScores[i] + wordScores[i + 1]
      );
    }

    for (let i = 0; i < words.length - 2; i++) {
      contributions.set(
        `${words[i]} ${words[i + 1]} ${words[i + 2]}`,
        wordScores[i] + wordScores[i + 1] + wordScores[i + 2]
      );
    }

    return contributions;
  }

  /**
   * Search for similar records using semantic similarity
   */
  async search(query: SearchQuery): Promise<SearchResult[]> {
    console.log(
      `🔍 Debug: Starting search for "${query.text.slice(0, 50)}..." with filters:`,
      query.filters
    );

    if (this.initializationFailed || !this.isInitialized) {
      console.warn('🔄 Semantic search not available, returning empty results');
      return [];
    }

    if (this.embeddedRecords.length === 0) {
      console.warn('⚠️ No embedded records available, returning empty results');
      return [];
    }

    console.log(`🔍 Debug: Total embedded records: ${this.embeddedRecords.length}`);

    // Generate embedding for query
    const queryEmbedding = await this.generateEmbedding(query.text);
    const queryNorm = this.computeNorm(queryEmbedding);

    console.log(
      `🔍 Debug: Query embedding dimensions: ${queryEmbedding.length}, norm: ${queryNorm.toFixed(4)}`
    );

    // Filter records based on query filters
    let filteredRecords = this.embeddedRecords;
    console.log(`🔍 Debug: Before filtering: ${filteredRecords.length} records`);

    if (query.filters?.types) {
      filteredRecords = filteredRecords.filter(er =>
        query.filters!.types!.includes(er.record.type)
      );
      console.log(
        `🔍 Debug: After type filtering (${query.filters.types.join(', ')}): ${filteredRecords.length} records`
      );
    }

    if (query.filters?.collections && filteredRecords.length > 0) {
      filteredRecords = filteredRecords.filter(er => {
        const rule = er.record as any;
        return rule.collection && query.filters!.collections!.includes(rule.collection);
      });
    }

    if (query.filters?.categories && filteredRecords.length > 0) {
      filteredRecords = filteredRecords.filter(er => {
        const entry = er.record as any;
        return entry.category && query.filters!.categories!.includes(entry.category);
      });
    }

    // Compute similarities and create search results
    const results: SearchResult[] = filteredRecords.map(embeddedRecord => {
      const similarity = this.computeCosineSimilarity(
        queryEmbedding,
        embeddedRecord.embedding,
        queryNorm,
        embeddedRecord.norm
      );

      return {
        record: embeddedRecord.record,
        similarity,
        relevance_score: similarity, // Can be enhanced with additional scoring
      };
    });

    // Show some similarity samples
    const sortedForDebug = [...results].sort((a, b) => b.similarity - a.similarity);
    console.log(
      `🔍 Debug: Top 5 similarities:`,
      sortedForDebug
        .slice(0, 5)
        .map(
          r =>
            `${r.similarity.toFixed(4)} (${r.record.type} ${String(r.record.id || 'no-id').slice(0, 20)})`
        )
        .join(', ')
    );

    // Sort by similarity (NO threshold filtering - pure top-k approach)
    const sortedResults = results.sort((a, b) => b.similarity - a.similarity);

    console.log(
      `🔍 Debug: Sorted ${sortedResults.length} results by similarity (no threshold filtering)`
    );

    // Return top results
    const maxResults = query.max_results || 10;
    const finalResults = sortedResults.slice(0, maxResults);

    console.log(`🔍 Debug: Returning ${finalResults.length} final results (max: ${maxResults})`);
    if (finalResults.length > 0) {
      console.log(
        `🔍 Debug: Final results:`,
        finalResults
          .map(
            r =>
              `${r.similarity.toFixed(4)} (${r.record.type} ${String(r.record.id || 'no-id').slice(0, 15)})`
          )
          .join(', ')
      );
    }

    return finalResults;
  }

  /**
   * Get statistics about loaded records
   */
  getStats() {
    const typeStats = this.embeddedRecords.reduce(
      (stats, er) => {
        stats[er.record.type] = (stats[er.record.type] || 0) + 1;
        return stats;
      },
      {} as Record<string, number>
    );

    return {
      total_records: this.embeddedRecords.length,
      by_type: typeStats,
      model: this.modelName,
      initialized: this.isInitialized,
    };
  }
}
